import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';

interface FavRecord {
  userId: string;
  propertyId: string;
  createdAt: string;
  property: {
    id: string; title: string; city: string | null; district: string | null;
    type: string; purpose: string;
    price: number | string; area: number; rooms: number; bathrooms: number;
    coverImage: string | null;
    status: string;
  };
}

export function MyFavoritesPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['my-favorites'],
    queryFn: () => api.get<ApiSuccess<FavRecord[]>>('/api/favorites'),
  });

  const removeM = useMutation({
    mutationFn: (propId: string) => api.delete(`/api/favorites/${propId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-favorites'] }),
  });

  const list = data?.data ?? [];

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-2">❤️ المفضلة</h1>
      <p className="text-ink-secondary mb-6">العقارات اللي أعجبتك وحفظتها للرجوع إليها لاحقاً</p>

      {isLoading ? (
        <div className="text-center py-12 text-ink-muted">⟳ جاري التحميل…</div>
      ) : list.length === 0 ? (
        <Card><CardBody>
          <div className="text-center py-16">
            <div className="text-7xl mb-4">💔</div>
            <h3 className="text-xl font-black text-navy-dark mb-2">قائمتك فارغة</h3>
            <p className="text-sm text-ink-muted mb-4">اضغط القلب على أي عقار لإضافته للمفضلة</p>
            <Link to="/properties"><Button variant="primary">🏘️ تصفّح العقارات</Button></Link>
          </div>
        </CardBody></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((f) => (
            <Card key={f.propertyId} className="overflow-hidden hover:shadow-brand-lg transition">
              <Link to={`/properties/${f.property.id}`}>
                <div className="h-44 bg-navy-soft relative">
                  {f.property.coverImage ? (
                    <img src={f.property.coverImage} alt={f.property.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl text-ink-muted">🏠</div>
                  )}
                  <div className="absolute top-2 right-2">
                    <Badge color={f.property.status === 'ACTIVE' ? 'green' : 'amber'}>{f.property.status}</Badge>
                  </div>
                </div>
              </Link>
              <CardBody>
                <h3 className="font-black text-base mb-1 line-clamp-1">{f.property.title}</h3>
                <p className="text-xs text-ink-muted mb-2">{f.property.city} {f.property.district && `· ${f.property.district}`}</p>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xl font-black text-navy-dark">{Number(f.property.price).toLocaleString('en-US')} <span className="text-xs font-normal">ر.س</span></span>
                  <span className="text-xs text-ink-muted">{f.property.area} م²</span>
                </div>
                <div className="flex gap-1 text-xs text-ink-muted mb-3">
                  <span>🛏️ {f.property.rooms}</span>
                  <span>·</span>
                  <span>🚿 {f.property.bathrooms}</span>
                </div>
                <div className="flex gap-2">
                  <Link to={`/properties/${f.property.id}`} className="flex-1">
                    <Button size="sm" variant="primary" className="w-full">تفاصيل</Button>
                  </Link>
                  <Button size="sm" variant="ghost" onClick={() => removeM.mutate(f.propertyId)}>💔</Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
